import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './DirectorioUniversidades.module.css';

const cleanPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') return phone;
    
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');
    
    if (cleaned.startsWith('+52')) {
        cleaned = cleaned.substring(3);
    }
    else if (cleaned.startsWith('52') && cleaned.length === 12) {
        cleaned = cleaned.substring(2);
    }
    
    return cleaned;
};

const cleanPhoneArray = (phoneArray) => {
    if (!Array.isArray(phoneArray)) return phoneArray;
    return phoneArray.map(phone => cleanPhoneNumber(phone));
};

const DirectorioUniversidades = () => {
    const navigate = useNavigate();
    const [selectedUniversity, setSelectedUniversity] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [universities, setUniversities] = useState([]);

    useEffect(() => {
        const fetchUniversidades = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-universidades`);
                if (!response.ok) {
                    throw new Error('Error al obtener las carreras');
                }
                const data = await response.json();
                setUniversities(data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };
        fetchUniversidades();
    }, []);

    const navigateTo = (route) => {
        navigate(route);
    };

    if (loading) {
        return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContent}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>Cargando universidades...</p>
            </div>
        </div>
        );
    }

    if (error) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <p className={styles.loadingText}>Error: {error}</p>
                </div>
            </div>
        );
    }

    const handleCardClick = (university) => {
        setSelectedUniversity(university);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUniversity(null);
    };

    const getGeneralInfo = (university) => {
    const allPhones = university.planteles
        .filter(plantel => plantel.phone && plantel.phone.length > 0)
        .flatMap(plantel => cleanPhoneArray(plantel.phone)); // ← Cambio aquí
    
    const allEmails = university.planteles
        .filter(plantel => plantel.email && plantel.email.length > 0)
        .flatMap(plantel => plantel.email);
    
    const uniquePhones = [...new Set(allPhones)];
    const uniqueEmails = [...new Set(allEmails)];
    
    const plantelesWithPhone = university.planteles.filter(p => p.phone && p.phone.length > 0);
    const plantelesWithEmail = university.planteles.filter(p => p.email && p.email.length > 0);
    
    const hasCommonPhone = plantelesWithPhone.length > 0 && 
        plantelesWithPhone.every(plantel => {
            const cleanedPhones = cleanPhoneArray(plantel.phone); // ← Cambio aquí
            return cleanedPhones.length === uniquePhones.length &&
                cleanedPhones.every(phone => uniquePhones.includes(phone));
        });
    
    const hasCommonEmail = plantelesWithEmail.length > 0 && 
        plantelesWithEmail.every(plantel => 
            plantel.email.length === uniqueEmails.length &&
            plantel.email.every(email => uniqueEmails.includes(email))
        );
    
    return {
        commonPhones: hasCommonPhone ? uniquePhones : [],
        commonEmails: hasCommonEmail ? uniqueEmails : [],
        website: university.contact.website
    };
    };

    const getPlantelSpecificInfo = (plantel, generalInfo) => {
        const plantelPhones = cleanPhoneArray(plantel.phone || []); // ← Cambio aquí
        const plantelEmails = plantel.email || [];
        
        const specificPhones = generalInfo.commonPhones.length === 0 ? plantelPhones : [];
        const specificEmails = generalInfo.commonEmails.length === 0 ? plantelEmails : [];
        
        return {
            specificPhones,
            specificEmails
        };
    };

    const UniversityCard = ({ university }) => {
        return (
            <div
                className={styles.universityCard}
                onClick={() => handleCardClick(university)}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(university);
                    }
                }}
            >
                <h3 className={styles.universityCardTitle}>{university.name}</h3>
              <img
    alt={`Logo de ${university.name}`}
    src={`${process.env.REACT_APP_API_URL}/universidades/logos-universidades/${university.logo}`}
    className={styles.universityCardLogo}
    onError={(e) => {
        e.target.onerror = null; // 
        e.target.src = 'https://via.placeholder.com/150?text=Sin+Logo';
    }}
/>
            </div>
        );
    };
    const Modal = ({ isOpen, university, onClose }) => {
        if (!isOpen || !university) return null;

        const generalInfo = getGeneralInfo(university);
        const hasMultipleCampuses = university.planteles.length > 1;

        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <button
                            className={styles.modalClose}
                            onClick={onClose}
                            type="button"
                            aria-label="Cerrar modal"
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        <h2 className={styles.modalTitle}>{university.name}</h2>
                        
                        {(generalInfo.commonPhones.length > 0 || generalInfo.commonEmails.length > 0 || hasMultipleCampuses) && (
                            <div className={styles.generalInfoSection}>
                                <h3 className={styles.sectionTitle}>Información General</h3>
                                <div className={styles.modalDetails}>
                                    {generalInfo.commonEmails.length > 0 && (
                                        <div className={styles.modalDetail}>
                                            <strong>Correo:</strong>{' '}
                                            {generalInfo.commonEmails.map((email, idx) => (
                                                <span key={idx}>
                                                    <a href={`mailto:${email}`} className={styles.modalLink}>
                                                        {email}
                                                    </a>
                                                    {idx < generalInfo.commonEmails.length - 1 && ', '}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className={styles.modalDetail}>
                                        <strong>Página web:</strong>{' '}
                                        <a
                                            href={generalInfo.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.modalLink}
                                        >
                                            {generalInfo.website}
                                        </a>
                                    </div>
                                    {generalInfo.commonPhones.length > 0 && (
                                        <div className={styles.modalDetail}>
                                            <strong>Teléfono:</strong> {generalInfo.commonPhones.join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {!hasMultipleCampuses ? (
                            <div className={styles.locationSection}>
                                <h3 className={styles.sectionTitle}>Ubicación</h3>
                                <div className={styles.singleCampusLayout}>
                                    <div className={styles.campusMap}>
                                        <iframe
                                            src={university.planteles[0].mapEmbed}
                                            width="100%"
                                            height="300"
                                            style={{
                                                border: 0,
                                                borderRadius: '0.75rem',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            }}
                                            allowFullScreen=""
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            title={`Mapa de ${university.name}`}
                                        />
                                    </div>
                                    
                                    <div className={styles.campusInfo}>
                                        <div className={styles.modalDetail}>
                                            <strong>Dirección:</strong> {university.planteles[0].address}
                                        </div>
                                        
                                        {generalInfo.commonPhones.length === 0 && university.planteles[0].phone && university.planteles[0].phone.length > 0 && (
                                            <div className={styles.modalDetail}>
                                                <strong>Teléfono{university.planteles[0].phone.length > 1 ? 's' : ''}:</strong> {cleanPhoneArray(university.planteles[0].phone).join(', ')}
                                            </div>
                                        )}
                                        
                                        {generalInfo.commonEmails.length === 0 && university.planteles[0].email && university.planteles[0].email.length > 0 && (
                                            <div className={styles.modalDetail}>
                                                <strong>Correo{university.planteles[0].email.length > 1 ? 's' : ''}:</strong>{' '}
                                                {university.planteles[0].email.map((email, idx) => (
                                                    <span key={idx}>
                                                        <a href={`mailto:${email}`} className={styles.modalLink}>
                                                            {email}
                                                        </a>
                                                        {idx < university.planteles[0].email.length - 1 && ', '}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {generalInfo.commonPhones.length === 0 && generalInfo.commonEmails.length === 0 && (
                                            <div className={styles.modalDetail}>
                                                <strong>Página web:</strong>{' '}
                                                <a
                                                    href={generalInfo.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.modalLink}
                                                >
                                                    {generalInfo.website}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.plantelesSection}>
                                <h3 className={styles.sectionTitle}>Planteles</h3>
                                
                                {university.planteles.map((plantel, index) => {
                                    const specificInfo = getPlantelSpecificInfo(plantel, generalInfo);
                                    
                                    return (
                                        <div key={plantel.id} className={styles.plantelSection}>
                                            
                                            
                                            <div className={styles.plantelContent}>
                                                <div className={styles.plantelMap}>
                                                    <iframe
                                                        src={plantel.mapEmbed}
                                                        width="100%"
                                                        height="250"
                                                        style={{
                                                            border: 0,
                                                            borderRadius: '0.75rem',
                                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                        }}
                                                        allowFullScreen=""
                                                        loading="lazy"
                                                        referrerPolicy="no-referrer-when-downgrade"
                                                        title={`Mapa del plantel ${index + 1}`}
                                                    />
                                                </div>
                                                
                                                <div className={styles.plantelInfo}>
                                                    <div className={styles.modalDetail}>
                                                        <strong>Dirección:</strong> {plantel.address}
                                                    </div>
                                                    
                                                    {specificInfo.specificPhones.length > 0 && (
                                                        <div className={styles.modalDetail}>
                                                            <strong>Teléfono{specificInfo.specificPhones.length > 1 ? 's' : ''}:</strong> {specificInfo.specificPhones.join(', ')}
                                                        </div>
                                                    )}
                                                    
                                                    {specificInfo.specificEmails.length > 0 && (
                                                        <div className={styles.modalDetail}>
                                                            <strong>Correo{specificInfo.specificEmails.length > 1 ? 's' : ''}:</strong>{' '}
                                                            {specificInfo.specificEmails.map((email, idx) => (
                                                                <span key={idx}>
                                                                    <a href={`mailto:${email}`} className={styles.modalLink}>
                                                                        {email}
                                                                    </a>
                                                                    {idx < specificInfo.specificEmails.length - 1 && ', '}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    <div className={styles.grid}>
                        {universities.map((university) => (
                            <UniversityCard key={university.id} university={university} />
                        ))}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                university={selectedUniversity}
                onClose={closeModal}
            />
        </div>
    );
};

export default DirectorioUniversidades;